import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Spin, Table, Tag, Typography, message, Grid } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, MailOutlined, TeamOutlined, UserAddOutlined } from '@ant-design/icons';
import { shopTeamApi } from '../utils/api';
import ColumnsCardList from '../components/common/ColumnsCardList';

const ROLE_LABELS = { owner: 'Propriétaire', admin: 'Administrateur', seller: 'Vendeur' };
const ROLE_COLORS = { owner: 'gold', admin: 'purple', seller: 'blue' };
const INVITABLE_ROLES = ['admin', 'seller'];

/** Affiche et administre les membres, rôles et invitations de la boutique choisie. */
const ShopTeamManagement = () => {
  // Mobile (< md) : tableaux remplacés par des cartes ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { shopId: routeShopId } = useParams();
  const location = useLocation();
  // La route du menu n'a pas d'identifiant : elle utilise alors la boutique active.
  const shopId = routeShopId || window.localStorage.getItem('activeShopId');
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form] = Form.useForm();

  /** Charge l'équipe à partir de l'API et présente toute erreur à proximité de l'action. */
  const loadTeam = async () => {
    setLoading(true);
    try {
      const response = await shopTeamApi.get(shopId);
      setTeam(response.data);
    } catch (error) {
      message.error(error.response?.data?.error || 'Impossible de charger l’équipe.');
    } finally {
      setLoading(false);
    }
  };

  /** Recharge l'équipe lorsque la boutique indiquée par l'URL change. */
  useEffect(() => {
    loadTeam();
  }, [shopId]);

  /** Envoie l'invitation après la validation visible du formulaire e-mail et rôle. */
  const submitInvitation = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await shopTeamApi.invite(shopId, values);
      message.success('Invitation envoyée par e-mail.');
      form.resetFields();
      setInviteOpen(false);
      await loadTeam();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.error || 'Impossible d’envoyer l’invitation.');
    } finally {
      setSaving(false);
    }
  };

  /** Change le rôle du membre sélectionné, puis rafraîchit les droits affichés. */
  const changeRole = async (memberId, role) => {
    try {
      await shopTeamApi.updateMemberRole(shopId, memberId, role);
      message.success('Rôle mis à jour.');
      await loadTeam();
    } catch (error) {
      message.error(error.response?.data?.error || 'Impossible de modifier ce rôle.');
    }
  };

  /** Retire un membre uniquement après une confirmation explicite de l'administrateur. */
  const removeMember = async (memberId) => {
    try {
      await shopTeamApi.removeMember(shopId, memberId);
      message.success('Membre retiré de la boutique.');
      await loadTeam();
    } catch (error) {
      message.error(error.response?.data?.error || 'Impossible de retirer ce membre.');
    }
  };

  /** Annule une invitation qui n'a pas encore été acceptée par son destinataire. */
  const cancelInvitation = async (invitationId) => {
    try {
      await shopTeamApi.cancelInvitation(shopId, invitationId);
      message.success('Invitation annulée.');
      await loadTeam();
    } catch (error) {
      message.error(error.response?.data?.error || 'Impossible d’annuler cette invitation.');
    }
  };

  const canManage = ['owner', 'admin'].includes(team?.currentRole);
  /** Détermine si le rôle connecté peut agir sur le rôle affiché dans la ligne. */
  const canManageTarget = (role) => team?.currentRole === 'owner' ? role !== 'owner' : role === 'seller';
  const memberColumns = [
    { title: 'Membre', key: 'member', render: (_, member) => <div><strong>{member.name || '—'}</strong><br /><span className="text-gray-500">{member.email}</span></div> },
    { title: 'Rôle', dataIndex: 'role', key: 'role', render: (role, member) => canManage && canManageTarget(member.role) ? <Select aria-label={`Rôle de ${member.email}`} value={role} onChange={(value) => changeRole(member.id, value)} options={INVITABLE_ROLES.map((value) => ({ value, label: ROLE_LABELS[value] }))} /> : <Tag color={ROLE_COLORS[role]}>{ROLE_LABELS[role]}</Tag> },
    { title: 'Action', key: 'action', render: (_, member) => canManage && canManageTarget(member.role) ? <Popconfirm title="Retirer ce membre de la boutique ?" okText="Retirer" cancelText="Annuler" onConfirm={() => removeMember(member.id)}><Button danger icon={<DeleteOutlined />} aria-label={`Retirer ${member.email}`}>Retirer</Button></Popconfirm> : '—' }
  ];

  const invitationColumns = [
    { title: 'E-mail', dataIndex: 'email', key: 'email' },
    { title: 'Rôle', dataIndex: 'role', key: 'role', render: (role) => <Tag color={ROLE_COLORS[role]}>{ROLE_LABELS[role]}</Tag> },
    { title: 'Expiration', dataIndex: 'expires_at', key: 'expires_at', render: (date) => new Date(date).toLocaleDateString('fr-FR') },
    { title: 'Action', key: 'action', render: (_, invitation) => canManage ? <Popconfirm title="Annuler cette invitation ?" okText="Annuler l’invitation" cancelText="Retour" onConfirm={() => cancelInvitation(invitation.id)}><Button danger size="small">Annuler</Button></Popconfirm> : '—' }
  ];

  if (!shopId) return <Navigate to="/app/select-shop" replace />;

  return (
    <div className="p-4 sm:p-6">
      <Space className="mb-5" wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(location.pathname === '/app/users' ? '/app/dashboard' : '/account/shops')}>Boutiques</Button>
        <Typography.Title level={isMobile ? 4 : 2} className="!mb-0"><TeamOutlined /> Équipe {team?.shop?.name ? `— ${team.shop.name}` : ''}</Typography.Title>
      </Space>
      <Spin spinning={loading}>
        <Card title="Membres" extra={canManage && <Button type="primary" icon={<UserAddOutlined />} onClick={() => setInviteOpen(true)}>Inviter un membre</Button>}>
          {isMobile ? (
            <ColumnsCardList columns={memberColumns} dataSource={team?.members || []} headerKeys={['member']} actionKeys={['action']} emptyText="Aucun membre" />
          ) : (
            <Table columns={memberColumns} dataSource={team?.members || []} rowKey="id" pagination={false} scroll={{ x: 700 }} />
          )}
        </Card>
        <Card title="Invitations en attente" className="mt-5">
          {isMobile ? (
            <ColumnsCardList columns={invitationColumns} dataSource={team?.invitations || []} headerKeys={['email']} actionKeys={['action']} emptyText="Aucune invitation en attente" />
          ) : (
            <Table rowKey="id" dataSource={team?.invitations || []} pagination={false} scroll={{ x: 600 }} columns={invitationColumns} />
          )}
        </Card>
      </Spin>
      <Modal title="Inviter un membre" open={inviteOpen} confirmLoading={saving} okText="Envoyer l’invitation" cancelText="Annuler" onOk={submitInvitation} onCancel={() => { form.resetFields(); setInviteOpen(false); }}>
        <Form form={form} layout="vertical" initialValues={{ role: 'seller' }}>
          <Form.Item label="Adresse e-mail" name="email" rules={[{ required: true, message: 'L’adresse e-mail est obligatoire.' }, { type: 'email', message: 'Saisissez une adresse e-mail valide.' }]}>
            <Input prefix={<MailOutlined />} type="email" autoComplete="email" placeholder="collaborateur@exemple.com" />
          </Form.Item>
          <Form.Item label="Rôle" name="role" rules={[{ required: true, message: 'Choisissez un rôle.' }]}>
            <Select options={INVITABLE_ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }))} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ShopTeamManagement;
